import { useEffect, useRef, useState } from "react";
import { exportToBlob, MIME_TYPES } from "@excalidraw/excalidraw";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import { getDataURL } from "@excalidraw/excalidraw/data/blob";
import { v4 as uuidv4 } from "uuid";

import { processImage } from "@snapnote/app/lib/processImage";
import { upsertDiagram } from "@snapnote/app/data/diagrams";

import type { NonDeletedExcalidrawElement } from "@excalidraw/element/types";

import { atom, useAtom } from "../../editor-jotai";
import { t } from "../../i18n";
import { useApp, useExcalidrawSetAppState } from "../App";

import { Dialog } from "../Dialog";
import { ArrowRightIcon } from "../icons";

import { insertToEditor } from "../TTDDialog/common";

import { CaptureDialogInput } from "./CaptureDialogInput";
import { CaptureDialogOutput } from "./CaptureDialogOutput";
import { CaptureDialogPanels } from "./CaptureDialogPanels";
import { CaptureDialogPanel } from "./CaptureDialogPanel";

import "./CaptureDialog.scss";

import type { ImageToExcalidrawLibProps } from "./imageToExcalidraw";
import type { BinaryFiles } from "../../types";

const rateLimitsAtom = atom<{
  rateLimit: number;
  rateLimitRemaining: number;
} | null>(null);

const captureGenerationAtom = atom<{
  generatedResponse: string | null;
  prompt: string | null;
} | null>(null);

async function maybeSaveDiagram(elements: NonDeletedExcalidrawElement[]) {
  if (!window.confirm("Save this diagram to your library?")) {
    return;
  }

  const name =
    window.prompt("Enter diagram name") ||
    `Diagram ${new Date().toLocaleString()}`;
  const appState = getDefaultAppState();
  const blob = await exportToBlob({
    elements,
    appState: {
      ...appState,
      exportBackground: true,
      viewBackgroundColor: appState.viewBackgroundColor,
    },
    files: {},
    mimeType: MIME_TYPES.png,
  });
  const thumbnail = await getDataURL(blob);

  upsertDiagram({
    id: uuidv4(),
    name,
    elements,
    appState: {
      ...appState,
      width: window.innerWidth,
      height: window.innerHeight,
      offsetTop: 0,
      offsetLeft: 0,
    },
    thumbnail,
  });
}

export const CaptureDialog = () => {
  const app = useApp();
  const setAppState = useExcalidrawSetAppState();

  const [rateLimits] = useAtom(rateLimitsAtom);

  const [onImageSubmitInProgess, setOnImageSubmitInProgess] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // 画像入力（カメラのみ）
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  // AI 処理
  const [isProcessing, setIsProcessing] = useState(false);
  const [shapes, setShapes] = useState<NonDeletedExcalidrawElement[]>([]);
  const [imageSizeRatio, setImageSizeRatio] = useState(1);

  const data = useRef<{
    elements: readonly NonDeletedExcalidrawElement[];
    files: BinaryFiles | null;
  }>({
    elements: [],
    files: null,
  });
  const canvasRef = useRef<HTMLDivElement>(null);

  // ライブラリロード（出力側で使うロード状態）
  const [imageToExcalidrawLib, setImageToExcalidrawLib] =
    useState<ImageToExcalidrawLibProps>({
      loaded: false,
      api: import("@excalidraw/mermaid-to-excalidraw"),
    });

  useEffect(() => {
    (async () => {
      await imageToExcalidrawLib.api;
      setImageToExcalidrawLib((prev) => ({ ...prev, loaded: true }));
    })();
  }, [imageToExcalidrawLib.api]);

  // カメラコールバック
  const handleCapture = (dataUrl: string) => {
    setCapturedImage(dataUrl);
    setImageUrl(dataUrl);
    setError(null);

    // ざっくり縦横比更新（16:9想定のプレースホルダとして）
    // 実画像読み込みで厳密に取りたい場合は Image() で onload してもOK
    setImageSizeRatio(16 / 9);
  };
  const handleRetake = () => {
    setCapturedImage(null);
    setImageUrl(null);
    setShapes([]);
    setError(null);
  };

  useEffect(() => {
    if (process.env.NODE_ENV === "development" && !capturedImage) {
      (async () => {
        try {
          const res = await fetch("/screenshots/shapes.png");
          const blob = await res.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            const dataUrl = reader.result as string;
            setCapturedImage(dataUrl);
            setImageUrl(dataUrl);
            setError(null);
            const img = new Image();
            img.onload = () => {
              setImageSizeRatio(img.width / img.height);
            };
            img.src = dataUrl;
          };
          reader.readAsDataURL(blob);
        } catch (err) {
          console.error("Failed to load sample image:", err);
        }
      })();
    }
  }, [capturedImage]);

  // AI処理
  const onProcess = async () => {
    if (!imageUrl) {
      setError(new Error("No image captured. Please capture an image first."));
      return;
    }
    setOnImageSubmitInProgess(true);
    setIsProcessing(true);
    setError(null);

    try {
      const result = await processImage(imageUrl);
      if (!result || result.length === 0) {
        setShapes([]);
        data.current.elements = [];
        setError(
          new Error(
            "No shapes were detected in the image. Try another capture.",
          ),
        );
      } else {
        setShapes(result);
        data.current.elements = result;
        await maybeSaveDiagram(result);
      }
    } catch (err) {
      console.error("Error processing image:", err);
      setShapes([]);
      data.current.elements = [];
      setError(
        new Error(
          "Failed to process image. The AI service may be temporarily unavailable. You can still add shapes manually.",
        ),
      );
    } finally {
      setIsProcessing(false);
      setOnImageSubmitInProgess(false);
    }
  };

  const vw = typeof window !== "undefined" ? window.innerWidth : 0;

  return (
    <Dialog
      className="capture-dialog"
      onCloseRequest={() => {
        app.setOpenDialog(null);
      }}
      size={1200}
      title={t("capture.title")}
      autofocus={false}
    >
      <div className="CaptureDialog">
        <div className="capture-dialog-desc">{t("capture.desc")}</div>
        <CaptureDialogPanels>
          {/* 左：カメラ入力 */}
          <CaptureDialogPanel
            label={t("labels.camera")}
            panelAction={{
              action: onProcess,
              label: t("labels.process"),
              icon: ArrowRightIcon,
            }}
            onImageSubmitInProgess={onImageSubmitInProgess}
            renderTopRight={() =>
              rateLimits ? (
                <div
                  className="capture-dialog-rate-limit"
                  style={{
                    fontSize: 12,
                    marginLeft: "auto",
                    color:
                      rateLimits.rateLimitRemaining === 0
                        ? "var(--color-danger)"
                        : undefined,
                  }}
                >
                  {rateLimits.rateLimitRemaining} requests left today
                </div>
              ) : null
            }
          >
            <CaptureDialogInput
              capturedImage={capturedImage}
              onCapture={handleCapture}
              onRetake={handleRetake}
            />
          </CaptureDialogPanel>
          {/* 右：プレビュー／挿入 */}
          <CaptureDialogPanel
            label={t("labels.preview")}
            panelAction={{
              action: () => insertToEditor({ app, data }),
              label: t("labels.insert"),
              icon: ArrowRightIcon,
            }}
          >
            <CaptureDialogOutput
              error={error}
              canvasRef={canvasRef}
              loaded={imageToExcalidrawLib.loaded && !isProcessing}
            />
          </CaptureDialogPanel>
        </CaptureDialogPanels>
        {/* Capture functionality will be implemented here */}
      </div>
    </Dialog>
  );
};
